/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { User, UserApiResponse } from '@core/model/user/user.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@env/environments';
import { DataTable, DataTableApiModel, DataTableComposer, Pagination } from '@core/model/data-table/data-table.model';
import { UserPreferencesModel } from "@core/model/user/user-preferences.model";

@Injectable({
  providedIn: 'root'
})
export class UserService {

  constructor(private http: HttpClient) {
  }

  /**
   * Get User By user ID
   * @return {{User}}
   */
  getUserById$(id?: string): Observable<User> {
    const userId: string | null = localStorage.getItem('user_id');
    return this.http.get<UserApiResponse>(`${environment.BASE_URL}/users/${id ? id : userId}`)
      .pipe(map((user: UserApiResponse): User => {
        const mappedUser = User.parseDTO(user);
        localStorage.setItem('isSso', user.isSso);
        return mappedUser;
      }));
  }

  /**
   * Get Users
   * @return Users Array
   */
  getUsers$(pagination: Pagination): Observable<DataTable<User>> {
    const params = new HttpParams({
      fromObject: {
        page: pagination.page,
        per_page: pagination.perPage!
      }
    });
    const orgId = localStorage.getItem('orgId');
    return this.http.get<DataTableApiModel<UserApiResponse>>(`${environment.BASE_URL}/users/?organizations.organization=${orgId}`, { params })
      .pipe(map((users: DataTableApiModel<UserApiResponse>): DataTable<User> => {
        const mappedDataResponse = {
          ...users,
          data: users.data.map(User.parseDTO)
        };
        return DataTableComposer.parseDto(mappedDataResponse);
      }));
  }

  /**
   * Update User
   * @param user
   */

  updateUser$(user: Partial<User>): Observable<User> {
    return this.http.put<UserApiResponse>(`${environment.BASE_URL}/users/${user.id}`, user)
      .pipe(map((response: UserApiResponse): User => {
        return User.parseDTO(response);
      }));
  }

  /**
   * Reset Password
   * @param email {{string}}
   */

  resetPassword$(email: string | undefined): Observable<string> {
    const payload = {
      email: email
    };
    return this.http.post<string>(`${environment.BASE_URL}/users/recovery-code`, payload)
      .pipe(map((res: string): string => {
        return res;
      }));
  }

  /**
   * Validate recovery code
   * @type Observable<string>
   * @param recoveryCode
   * @return res {{ string }}
   */
  validateRecoveryCode$(recoveryCode: string): Observable<string> {
    const payload = {
      recoveryCode: recoveryCode
    };
    return this.http.post<string>(`${environment.BASE_URL}/users/verify-recovery-code`, payload)
      .pipe(map((res: string): string => {
        return res;
      }));
  }

  /**
   * Reset Password
   * @param credentials
   * @param recoveryCode
   */

  doResetPassword(credentials: { password: string, passwordConfirmation: string }, recoveryCode: string): Observable<string> {
    const payload = {
      recovery: true,
      recoveryCode: recoveryCode,
      password: credentials.password,
      passwordConfirmation: credentials.passwordConfirmation
    };
    return this.http.put<string>(`${environment.BASE_URL}/users/password`, payload)
      .pipe(map((res: string): string => {
        return res;
      }));
  }

  /**
   * Change Password
   * @param userId
   * @param formValue
   */

  doChangePassword(userId: string | null, formValue: { oldPassword: string, password: string, passwordConfirmation: string }): Observable<string> {
    const payload = {
      id: userId,
      newPassword: formValue.password,
      passwordConfirmation: formValue.passwordConfirmation,
      previousPassword: formValue.oldPassword,
      recovery: false
    };
    return this.http.put<string>(`${environment.BASE_URL}/users/password`, payload)
      .pipe(map((res: string): string => {
        return res;
      }));
  }

  createUser$(formValue: any): Observable<User> {
    const orgId: string | null = localStorage.getItem('orgId');
    const payload = {
      ...formValue,
      conditionVersion: 1,
      organizations: [
        orgId
      ],
      gender: formValue.salutation,
      platformType: 'doctor', //Hardcoded until Business logic clarifications @Istvan
      twoFaMethod: 'email', //Hardcoded until Business logic clarifications @Istvan
      twoFa: true //Hardcoded until Business logic clarifications @Istvan
    };
    return this.http.post<UserApiResponse>(`${environment.BASE_URL}/users`, payload)
      .pipe(map((user: UserApiResponse): User => {
        return User.parseDTO(user);
      }));
  }

  addDefaultSuppliers(orgId: string, suppliersPreferences: UserPreferencesModel): Observable<UserPreferencesModel> {
    return this.http.post<UserPreferencesModel>(`${environment.BASE_URL}/organizations/${orgId}/preferences`, suppliersPreferences).
      pipe(map((res: UserPreferencesModel) => {
        return res;
    }))
  }

  updateDefaultSuppliers(orgId: string, suppliersPreferences: UserPreferencesModel, preferencesId: number): Observable<UserPreferencesModel> {
    return this.http.put<UserPreferencesModel>(`${environment.BASE_URL}/organizations/${orgId}/preferences/${preferencesId}`, suppliersPreferences).
    pipe(map((res: UserPreferencesModel) => {
      return res;
    }))
  }
}
